import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { parseSsoUser } from "./utils";
import { AuthContextValue, User } from "./types";
import { Nullish } from "@ubnt/store.core/lib/types";

const AuthContext = createContext<AuthContextValue | null>(null);

const handleLogin = (pathname?: string) => {
  const redirectUrl = new URL(window.origin);
  redirectUrl.pathname = pathname ?? new URL(window.location.href).pathname;

  const url = new URL("/login", __CONFIG__.ACCOUNT_BASE_URL);
  url.searchParams.set("redirect", redirectUrl.toString());

  window.location.replace(url);
};

const handleLogout = (pathname?: string) => {
  const redirectUrl = new URL(window.origin);
  redirectUrl.pathname = pathname ?? new URL(window.location.href).pathname;

  const url = new URL("/logout", __CONFIG__.ACCOUNT_BASE_URL);
  url.searchParams.set("redirect", redirectUrl.toString());

  window.location.replace(url);
};

const handleRegister = () => {
  const url = new URL("/register", __CONFIG__.ACCOUNT_BASE_URL);

  window.open(url, "_blank");
};

const handleManageAccount = () => {
  const url = new URL("/", __CONFIG__.ACCOUNT_BASE_URL);

  window.open(url, "_blank");
};

export const AuthContextProvider = ({ children }) => {
  // #############################################################################
  // User
  // #############################################################################
  const [user, setUser] = useState<Nullish<User>>(
    /**
     * User has 3 states - undefined, null, and Object
     * When undefined, the state is not yet handled
     * Object schema comes from `parseSsoUser` function
     */
    undefined,
  );

  const loading = useMemo(() => {
    return user === undefined;
  }, [user]);

  const isLoggedIn = useMemo(() => {
    return !!user;
  }, [user]);

  // #############################################################################
  // Get the auth state user
  // #############################################################################
  const handleGetUser = useCallback(async () => {
    const url = new URL(__CONFIG__.SSO_BASE_URL);
    url.pathname = "/api/sso/v1/user/self";

    try {
      const res = await fetch(url, {
        credentials: "include",
      });

      if (res.status !== 200) {
        throw new Error("User not found");
      }

      const data = await res.json();

      setUser(parseSsoUser(data));
    } catch {
      // User not found, set as null
      setUser(null);
    }
  }, []);

  useEffect(() => {
    handleGetUser();
  }, [handleGetUser]);

  // #############################################################################
  // Result
  // #############################################################################
  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isLoggedIn,
        handleLogin,
        handleLogout,
        handleRegister,
        handleManageAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === null) {
    throw new Error("useAuth must be used within the AuthContextProvider");
  }

  return context;
};
